import moment, { Moment } from 'moment';

/** Formaat van het tijdveld bij het invoegen van een rij (`<input type="time" step="1">`). */
export const EPHEMERIS_INSERT_TIME_FORMAT = 'HH:mm:ss';

const DAY_FORMAT = 'YYYY-MM-DD';
/** Het tijdveld laat de seconden weg wanneer ze 0 zijn. */
const TIME_FORMATS = [EPHEMERIS_INSERT_TIME_FORMAT, 'HH:mm'];
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Het venster waarin een rij ingevoegd wordt: strikt na `min` (de rij erboven) en strikt vóór
 * `max` (de rij eronder of het einde van de periode), in de weergavetijdzone.
 */
export interface EphemerisInsertWindow {
  readonly min: Moment;
  readonly max: Moment;
  readonly useUtc: boolean;
}

/** Kalenderdagen (`YYYY-MM-DD`, weergavetijdzone) die het venster raakt. */
export function ephemerisInsertDays(window: EphemerisInsertWindow): readonly string[] {
  const last = inZone(window.max.clone().subtract(1, 'ms'), window.useUtc).format(DAY_FORMAT);
  const days: string[] = [];
  for (const day = inZone(window.min, window.useUtc).startOf('day'); ; day.add(1, 'day')) {
    days.push(day.format(DAY_FORMAT));
    if (days[days.length - 1] >= last) return days;
  }
}

/**
 * Enkel een tijd volstaat zolang het venster korter is dan een dag: dan past een tijd op
 * hoogstens één dag binnen het venster (ook over middernacht heen). Pas bij een venster van
 * een dag of langer (bv. enkel trage planeten zichtbaar) moet ook de dag gekozen worden.
 */
export function ephemerisInsertNeedsDay(window: EphemerisInsertWindow): boolean {
  return window.max.diff(window.min) >= DAY_MS;
}

/**
 * Het moment bij `time` binnen het venster, of `undefined` als die tijd erbuiten valt.
 * Zonder `day` wordt de dag zelf gezocht (venster korter dan een dag); met `day` telt enkel die dag.
 */
export function resolveEphemerisInsertMoment(
  window: EphemerisInsertWindow,
  time: string,
  day?: string,
): Moment | undefined {
  if (!moment.utc(time, TIME_FORMATS, true).isValid()) return undefined;
  const candidates = day !== undefined ? [day] : ephemerisInsertDays(window);
  for (const candidate of candidates) {
    const formats = TIME_FORMATS.map(format => `${DAY_FORMAT}T${format}`);
    const at = window.useUtc
      ? moment.utc(`${candidate}T${time}`, formats, true)
      : moment(`${candidate}T${time}`, formats, true);
    if (at.isValid() && at.isAfter(window.min) && at.isBefore(window.max)) return at.utc();
  }
  return undefined;
}

function inZone(utc: Moment, useUtc: boolean): Moment {
  return useUtc ? utc.clone().utc() : utc.clone().local();
}
